import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, Outlet } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { useContent } from "../context/ContentContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/our-story", label: "Our Story" },
  { to: "/wedding-details", label: "Wedding Details" },
  { to: "/travel", label: "Travel" },
  { to: "/registry", label: "Registry" },
  { to: "/faq", label: "FAQ" },
];

function Announcement() {
  const { content } = useContent();
  const a = content?.announcement;
  if (!a?.enabled || !a?.message) return null;
  return (
    <div className="bg-olive text-soft-white py-2.5 px-4 text-sm tracking-wide flex items-center justify-center gap-4 flex-wrap" data-testid="announcement-banner">
      <span>{a.message}</span>
      <Link to="/rsvp" className="inline-flex items-center gap-1 bg-gold text-matte rounded-full px-4 py-1 overline hover:bg-soft-white transition-colors" data-testid="announcement-rsvp-btn">RSVP Now</Link>
    </div>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { content } = useContent();
  const logo = content?.home?.logo_image;
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-soft-white/95 backdrop-blur-md border-b border-taupe/30" : "bg-soft-white border-b border-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between" aria-label="Main navigation">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="Vernetta and Quin home">
          {logo
            ? <img src={logo} alt="Vernetta and Quin" className="h-12 w-auto" />
            : <span className="font-serif text-2xl tracking-wide text-matte">V <span className="text-gold">&amp;</span> Q</span>}
        </Link>
        <ul className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} end={n.to === "/"}
                className={({ isActive }) => `link-underline overline text-matte/80 hover:text-matte ${isActive ? "active text-matte" : ""}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                {n.label}
              </NavLink>
            </li>
          ))}
          <li>
            <Link to="/rsvp" className="btn-olive rounded-full px-6 py-2.5 overline" data-testid="nav-rsvp-cta">RSVP</Link>
          </li>
        </ul>
        <button className="lg:hidden p-2 text-matte" onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>
      {open && (
        <div className="lg:hidden bg-soft-white border-t border-taupe/30 px-5 pb-6 pt-2" data-testid="mobile-menu">
          <ul className="flex flex-col">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} end={n.to === "/"}
                  className={({ isActive }) => `block py-3 border-b border-taupe/20 text-base ${isActive ? "text-olive font-medium" : "text-matte/80"}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                  {n.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-4">
              <Link to="/rsvp" className="btn-olive rounded-full w-full block text-center py-3 overline" data-testid="mobile-nav-rsvp">RSVP</Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-matte text-soft-white/90 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-10 md:grid-cols-3">
        <div>
          <div className="font-serif text-3xl">Vernetta <span className="text-gold">&amp;</span> Quin</div>
          <p className="mt-3 text-sm text-soft-white/60">February 20, 2027</p>
          <p className="text-sm text-soft-white/60">The Metropolitan Club · Alpharetta, GA</p>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <span className="overline text-gold mb-1">Explore</span>
          <Link to="/rsvp" className="hover:text-gold w-fit" data-testid="footer-rsvp">RSVP</Link>
          <Link to="/registry" className="hover:text-gold w-fit" data-testid="footer-registry">Registry</Link>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <span className="overline text-gold mb-1">With Love</span>
          <p className="text-soft-white/70 flex items-center gap-2">
            <Heart size={15} className="text-gold" /> Made with love for our favorite people.
          </p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-soft-white/40">
        © 2027 Vernetta &amp; Quin
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="App marble-bg">
      <Announcement />
      <Nav />
      <main><Outlet /></main>
      <Footer />
    </div>
  );
}
